import { useEffect } from 'react';
import ClearPathLogo from '../components/ClearPathLogo';
import { PRO_PRICE_LABEL } from '../pricing';

/**
 * /pitch: print-ready one-pager for in-person sales.
 * Designed in ClearPath brand style.
 * Print tip: Chrome → Print → "More settings" → uncheck "Headers and footers"
 */
export default function PitchPage() {
  useEffect(() => {
    document.title = 'ClearPath: Business Intelligence for Independent Cafés & Coffee Shops';
  }, []);

  return (
    <div className="min-h-screen bg-white font-sans">
      {/* Print button, hidden when printing */}
      {/* In the page's flow on a phone, where a fixed bar covered the logo and the QR code */}
      <div className="print:hidden flex justify-end items-center gap-3 px-5 pt-4 sm:fixed sm:top-4 sm:right-4 sm:z-50 sm:p-0">
        <span className="hidden sm:inline text-sm text-[#6B6670]">Ready to print</span>
        <button
          onClick={() => window.print()}
          className="bg-[#4A2D5E] text-white text-sm font-medium px-4 min-h-[44px] rounded-[8px] hover:bg-[#3D254D] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4A2D5E]/40 focus-visible:ring-offset-2"
        >
          Print / Save PDF
        </button>
      </div>

      {/* One-pager: max A4 width, centered */}
      <div
        className="mx-auto max-w-[720px] min-h-screen flex flex-col px-5 py-8 sm:px-14 sm:py-12 print:px-14 print:py-12"
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-4 mb-10">
          <div>
            <ClearPathLogo variant="dark" size="lg" />
            <p className="mt-2 text-sm text-[#6B6670] font-sans">
              Business intelligence for independent cafés & coffee shops
            </p>
          </div>
          {/* QR code to clearpathsmb.com */}
          <div className="flex flex-col items-center gap-1.5 flex-shrink-0">
            <img
              src="/qr-clearpathsmb.svg"
              alt="QR code to clearpathsmb.com"
              width={90}
              height={90}
              className="rounded-[6px]"
            />
            <span className="text-[10px] text-[#6B6670] font-sans">clearpathsmb.com</span>
          </div>
        </div>

        {/* Hero statement */}
        <div className="mb-10 sm:pl-5">
          <h1 className="font-serif text-[28px] leading-tight text-[#2D2A2E] mb-3">
            Your POS collects everything.<br />
            Most of it never reaches you.
          </h1>
          <p className="text-[15px] text-[#6B6670] leading-relaxed max-w-lg">
            ClearPath turns your daily sales data into plain-English insights: what's selling,
            what's draining margin, and what to order next. It arrives every morning before you open.
          </p>
        </div>

        {/* Three columns: Problem / Solution / Result */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-10">
          {[
            {
              label: 'The problem',
              color: '#B54A4A',
              text: "Independent shop owners make inventory and pricing decisions on gut feel. There's no time to dig through exports.",
            },
            {
              label: 'The solution',
              color: '#4A2D5E',
              text: 'ClearPath syncs directly with Square and reads your data automatically. No spreadsheets. No setup headaches.',
            },
            {
              label: 'The result',
              color: '#2E6B4F',
              text: "A clear picture of your business every morning: what's working, what's not, and exactly what to do about it.",
            },
          ].map(({ label, color, text }) => (
            <div key={label} className="flex flex-col gap-2">
              <div
                className="text-[11px] font-semibold uppercase tracking-widest"
                style={{ color }}
              >
                {label}
              </div>
              <p className="text-[13px] text-[#2D2A2E] leading-relaxed">{text}</p>
            </div>
          ))}
        </div>

        {/* What you get */}
        <div className="mb-10">
          <h2 className="font-serif text-[16px] text-[#2D2A2E] mb-4">
            What's included
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
            {[
              ['Product Performance', "See exactly which items drive margin and which don't."],
              ['Daily Brief by email', 'Up to three moves each morning, each with a dollar range.'],
              ['Square POS Sync', 'Your sales data pulls in automatically, with no exports.'],
              ['Time Analysis', 'Know your slowest hours and busiest days at a glance.'],
              ['Financials Dashboard', 'Revenue, cost, and margin tracked in one place.'],
              ['Inventory Signals', 'Know what to reorder before you run out.'],
            ].map(([title, desc]) => (
              <div key={title} className="flex items-start gap-2.5">
                <div
                  className="w-4 h-4 rounded-full flex-shrink-0 mt-0.5 flex items-center justify-center"
                  style={{ backgroundColor: '#4A2D5E15' }}
                >
                  <div className="w-1.5 h-1.5 rounded-full bg-[#4A2D5E]" />
                </div>
                <div>
                  <div className="text-[13px] font-medium text-[#2D2A2E]">{title}</div>
                  <div className="text-[12px] text-[#6B6670]">{desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
          {[
            { stat: '16', label: 'deterministic detectors check your sales nightly and your supplier costs weekly. No AI guesses at the numbers' },
            { stat: '$2,500+', label: 'the low-end floor of a 26-week backtest on Morning Ritual Coffee Co., our modeled demo café, not a customer' },
            { stat: '2–3 weeks', label: 'of real sales measure every adopted move. The ones that don’t pan out count against the total' },
          ].map(({ stat, label }) => (
            <div
              key={stat}
              className="rounded-[10px] p-4 text-center"
              style={{ backgroundColor: '#4A2D5E08', border: '1px solid #4A2D5E18' }}
            >
              <div className="font-sans text-[26px] font-bold tabular-nums tracking-tight leading-none mb-1 text-[#4A2D5E]">
                {stat}
              </div>
              <div className="text-[11px] text-[#6B6670] leading-snug">{label}</div>
            </div>
          ))}
        </div>

        {/* Spacer to push footer down */}
        <div className="flex-1" />

        {/* Footer: pricing + contact */}
        <div
          className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 pt-6 mt-4"
          style={{ borderTop: '1px solid #E6E2DE' }}
        >
          <div>
            <div className="font-sans text-[22px] font-bold tabular-nums leading-none text-[#2D2A2E] mb-1">
              {PRO_PRICE_LABEL}<span className="text-base font-normal text-[#6B6670]">/month</span>
            </div>
            <div className="text-[12px] text-[#6B6670]">
              No contract · Cancel any time
            </div>
            <div className="text-[12px] text-[#6B6670] mt-0.5">
              One plan, everything included. Start today at{' '}
              <span className="text-[#4A2D5E] font-medium">clearpathsmb.com</span>
            </div>
          </div>
          <div className="sm:text-right">
            <div className="text-[12px] text-[#6B6670]">Questions?</div>
            <a
              href="mailto:support@clearpathsmb.com"
              className="text-[13px] text-[#4A2D5E] font-medium"
            >
              support@clearpathsmb.com
            </a>
          </div>
        </div>
      </div>

      {/* Print-only styles */}
      <style>{`
        @media print {
          @page {
            size: A4 portrait;
            margin: 0;
          }
          body {
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
        }
      `}</style>
    </div>
  );
}
